import { 
  db, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  serverTimestamp,
} from '../config/firebase';

export interface UserProfile {
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  photoURL?: string;
  isPremium?: boolean;
  premiumExpiryDate?: string;
  createdAt?: string;
  lastLoginAt?: string;
  preferences?: {
    emailNotifications?: boolean;
    pushNotifications?: boolean;
    theme?: 'light' | 'dark' | 'system';
  };
  [key: string]: any;
}

/**
 * Creates a new user profile in Firestore
 */
export const createUserProfile = async (userId: string, userData: UserProfile): Promise<void> => {
  try {
    console.log('Creating user profile for:', userId);
    const userRef = doc(db, 'users', userId);
    
    // Set default values for new users
    const userProfile: UserProfile = {
      ...userData,
      isPremium: false,
      createdAt: userData.createdAt || new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      preferences: {
        emailNotifications: true,
        pushNotifications: false,
        theme: 'system',
      }
    };
    
    await setDoc(userRef, userProfile);
    console.log('User profile created successfully');
    
    // Call Firebase function to send welcome email
    if (process.env.REACT_APP_FIREBASE_FUNCTIONS_URL) {
      try {
        console.log('Sending welcome email via Firebase Function');
        const response = await fetch(`${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/sendWelcomeEmail`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userData.email,
            name: userData.firstName,
          }),
        });
        
        if (response.ok) {
          console.log('Welcome email sent successfully');
        } else {
          console.error('Failed to send welcome email:', await response.text());
        }
      } catch (error) {
        // Don't throw error for non-critical operation
        console.error('Error sending welcome email:', error);
        console.log('Welcome email will be sent by the Firestore trigger instead');
      }
    } else {
      console.log('Firebase Functions URL not configured. Welcome email will be sent by the Firestore trigger instead.');
    }
    
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Gets a user profile from Firestore
 */
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data() as UserProfile;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

/**
 * Updates a user profile in Firestore
 */
export const updateUserProfile = async (userId: string, updates: Partial<UserProfile>): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Updates the last login timestamp for a user
 */
export const updateLastLogin = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastLoginAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating last login:', error);
    // Don't throw error for this non-critical operation
  }
};

/**
 * Upgrades a user to premium
 */
export const upgradeToPremium = async (userId: string, expiryDate: Date): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isPremium: true,
      premiumExpiryDate: expiryDate.toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error upgrading to premium:', error);
    throw error;
  }
}; 